import { all, fork, takeLatest} from 'redux-saga/effects';
import { AuthPayload } from 'actions/Auth';
import { CustomAction } from '../model/state';
import { AlertPayload, ConsumptionPayload, DeveloperMenuPayload, DevicePayload, LocationPayload, UserPayload } from 'actions';
import { 
    SIGNIN_USER_SUCCESS, 
    UPDATE_AUTH_USER_DATA_SUCCESS, 
    UPDATE_SHOW_LOCATION_FILTERS, 
    UPDATE_LOCATION_FILTERS, 
    SELECT_LOCATION_ROW, 
    SELECT_ALL_LOCATIONS, 
    SAVE_THRESHOLD_SETTINGS_SUCCESS,
    SET_CURRENT_ALERT,
    EDIT_LOCATION,
    SET_LOCATION_SYSTEM_MODE_BULK,
    UPDATE_NOTIFICATION_STATE,
    CREATE_LOCATION,
    REMOVE_LOCATIONS,
    ADD_LOCATIONS_TO_PARENT,
    UPDATE_ALERT_FILTER,
    FETCH_ALERT_EXPORT_CSV_DATA,
    CLEAR_ALERT_SUCCESS,
    CHANGE_VALVE_STATE,
    FETCH_CONSUMPTION_BY_LOCATION_SUCCESS,
    RUN_HEALTH_TEST,
    UPDATE_DEVICE,
    DELETE_DEVICE,
    UPDATE_DEVELOPER_MENU_SETTINGS,
    CREATE_USER,
    REVOKE_USER_INVITE,
    RESEND_USER_INVITE,
    SAVE_USER,
    FETCH_USER_LOCATIONS_ROLES_SUCCESS,
    EDIT_USER_ROLES,
    REMOVE_USER_SUCCESS,
    UPDATE_CONSUMPTION_FILTER,
    FETCH_CONSUMPTION_BY_FILTERS_SUCCESS,
    REQUEST_RESET_PASSWORD,
    SELECT_ALERT_ROW,
    SELECT_ALL_ALERTS,
} from '../constants/ActionTypes';

function identifyEventTriggerWorker(action: CustomAction<AuthPayload>) {
  action.payload?.user && window.analytics.identify(action.payload.user.id, {
    firstName: action.payload.user.firstName,
      lastName: action.payload.user.lastName,
      email: action.payload.user.email,   
  });
}

// Forget password
function trackResetPasswordWorker(action: CustomAction<AuthPayload>) {
  window.analytics.identify('Reset Password', {
    Email: action.payload?.email,
    Locale: action.payload?.locale,
  });
}

//Segment Track Action events functions for Location
function trackLocationFilterButtonWorker(action: CustomAction<LocationPayload>) {
  if (action.payload && action.payload.show) {
    window.analytics.track('Button Clicked', {
      Intent: 'Show Location Filters',
    });
  }
}

function trackLocationFilterSetWorker(action: CustomAction<LocationPayload>) {
    const filterSetMessage = {
      0: 'Location type set to Unit',
      1: 'Location type set to property', 
      2: 'Location type set to Region',
    }
    if (action.payload?.filters?.type) {
      window.analytics.track('Location Type Filter Set', {
        filter: `Location type set to ${filterSetMessage[action.payload.filters.type as keyof typeof filterSetMessage]}`,
      });
    } else if (action.payload?.filters?.cities && action.payload.filters.cities.length !== 0) {
      window.analytics.track('Location Cities Filter Set', {
        filter: `Loction cities set to ${action.payload.filters.cities}`,
      });
    } else if (action.payload?.filters?.states && action.payload.filters.states.length !== 0) {
      window.analytics.track('Location State Filter Set', {
        filter: `Loction states set to ${action.payload.filters.states}`,
      });
    } else if (action.payload?.filters?.countries && action.payload.filters.countries.length !== 0) {
      window.analytics.track('Location Countries Filter Set', {
        filter: `Loction countries set to ${action.payload.filters.countries}`,
      });
    } else if (action.payload?.filters?.zipCodes && action.payload.filters.zipCodes.length !== 0) {
      window.analytics.track('Location Zip Filter Set', {
        filter: `Loction zip codes set to ${action.payload.filters.zipCodes}`,
      });
    } else if (action.payload?.filters?.offlineDevices) {
      window.analytics.track('Location DeviceOffline Filter Set', {
        filter: `Device offline set to ${action.payload.filters.offlineDevices}`, 
      });
    };
}

function trackLocationCheckboxSelectedWorker(action: CustomAction<LocationPayload>) {
    if (action.type === 'select_location_row') {
      window.analytics.track('Location Checkbox Clicked', {
        Select_Location_Row: action.payload?.select,
        locationId: action.payload?.locationId,
      });
    } else {
      window.analytics.track('All Location Checkbox Clicked', {
        Select_All_Locations: action.payload?.selectAll,
      });
    };
}

function trackLocationActionTakenWorker(action: CustomAction<LocationPayload>) {
  if (action.payload) {
    if (action.type === 'edit_location') {
      window.analytics.track('Edit location', {
        Location_edited: action.payload.partialLocation,
    });
    } else if (action.type === 'save_threshold_settings_success' && action.payload.settings) {
      window.analytics.track('Set Location Threshold', {
        Threshold_Setting_Volume: action.payload.settings.volume.value, 
        Threshold_Setting_Duration: action.payload.settings.duration.value,
    });
    } else if(action.type === 'update_notification_state') {
      window.analytics.track('Updated Notification Settings', {
        larmId: action.payload.alarmId, 
        alarmSettingsPayload: action.payload.alarmSettingsPayload,
    });
    } else if (action.type === 'add_locations_to_parent') { 
        if (action.payload.parentType === 2) {
          window.analytics.track('Added Location to Region', {
            Location_Id: action.payload.locationIds,
          });
        } else {
          window.analytics.track('Added Location to Property', {
            Location_Id: action.payload.locationIds,
          });
        };    
    } else if (action.type === 'set_location_system_mode_bulk' && action.payload.revertMinutes === 1440) { 
      window.analytics.track('Location Sleep 24 Hours', {});
    } else if (action.type === 'set_location_system_mode_bulk' && action.payload.revertMinutes === 4320) { 
      window.analytics.track('Location Sleep 72 Hours', {});
    } else if (action.type === 'set_location_system_mode_bulk' && action.payload.systemModeTarget === 0) { 
      window.analytics.track('Set Location to Home Mode', {});
    } else if (action.type === 'set_location_system_mode_bulk' && action.payload.systemModeTarget === 1) { 
      window.analytics.track('Set Location to Away Mode', {});
    };
  };
}

function trackLocationCreatedOrRemovedWorker(action: CustomAction<LocationPayload>) {
  if (action.type === 'create_location') {
    window.analytics.track("Created Location Success", {
      Location: action.payload?.partialLocation,
    });
  } else if (action.type === 'remove_locations') {
    window.analytics.track("Removed Location Success", {});
  };
}

// Individual location unit page: 
function trackValveControlButtonWorker(action: CustomAction<DevicePayload>) {
  if (action.payload?.target === 1) {
    window.analytics.track("Turn Off Valve", {
      Location: action.payload.deviceId,
    });
  } else {
      window.analytics.track("Turn On Valve", {
        Location: action.payload?.deviceId, 
    });
  };
} 

function trackConsumptionChartScaleSetWorker(action: CustomAction<ConsumptionPayload>) {
  if (action.payload?.intervalType === 0) {
    window.analytics.track("Water Consumption Chart View Set to Today", {});
  } else if (action.payload?.intervalType === 1) {
    window.analytics.track("Water Consumption Chart View Set to Days", {});
  } else if (action.payload?.intervalType === 2) {
    window.analytics.track("Water Consumption Chart View Set to Weeks", {});
  } else {
    window.analytics.track("Water Consumption Chart View Set to Months", {});
  };
} 

function trackRunHealthTestWorker(action: CustomAction<DevicePayload>) {
  window.analytics.track("Health Test Run", {
    device: action.payload?.deviceId,
  });
} 

function trackRenameDeviceWorker(action: CustomAction<DevicePayload>) {
  window.analytics.track("Renamed Device", {
    Device: action.payload?.deviceId,
    New_Name: action.payload?.device?.nickname,
  });
} 

function trackUnpairDeviceWorker(action: CustomAction<DevicePayload>) {
  window.analytics.track("Unpaired Device", {
    Device: action.payload?.deviceId,
  });
} 

function trackDeveloperMenuOpenedWorker(action: CustomAction<DeveloperMenuPayload>) {
  window.analytics.track("Developer Menu Settings", {
    Device: action.payload?.deviceId,
    Settings: action.payload?.settings,
  });
} 

//Segment Track Action events functions for Alerts

function trackAlertsCheckboxSelectedWorker(action: CustomAction<AlertPayload>) {
  if (action.type === 'select_alert_row') {
    window.analytics.track('Alert Checkbox Clicked', {
      Alert_Row_selected: action.payload?.select,
      AlertId: action.payload?.alertId,
    });
  } else {
    window.analytics.track('All Alert Checkbox Clicked', {
      All_Alert_Row_selected: action.payload?.selectAll,
    });
  };
}

function trackViewAlertDetailWorker(action: CustomAction<AlertPayload>) {
  window.analytics.track('Viewed Alert Detail', {
    Alert: action.payload?.currentAlert?.displayTitle,
  });
}

function trackAlertFilterSetWorker(action: CustomAction<AlertPayload>) { 
  console.log(action.payload?.partialFilters?.locationIds);
  if (action.payload?.partialFilters?.locationIds?.length !== 0) {
    window.analytics.track('Alert Filter Set', {
      Filter: action.payload?.partialFilters, 
    });
  };
}

function trackAlertExportButtonClickedWorker(action: CustomAction<AlertPayload>) { 
  window.analytics.track('Button Clicked', {
    Intent: 'Alert CSV Exported',
    UserId: action.payload?.userId,
    Alert_Filter: action.payload?.filter,
  });
}

function trackActionOnSpecificAlertWorker(action: CustomAction<AlertPayload>) { 
  window.analytics.track('Resolved Alert', {
    Alert: action.payload?.alert,
  });
}

//Water Usage Page: 
function trackWaterUsageActionsWorker(action: CustomAction<ConsumptionPayload>) {
  if (action.type === 'update_consumption_filter') {
    window.analytics.track('Water Consumption Filter Set', {
      Filter: action.payload?.partialFilters,
    });
  } else if (action.type === 'fetch_consumption_by_filters_success') {
    window.analytics.track('Refreshed Water Consumption Data', {
      Filter: action.payload?.consumptionData,
    });
  };
}

// User Page: 
function trackUserActionWorker(action: CustomAction<UserPayload>) {
  if (action.type === 'create_user') {
    window.analytics.track('Added User', {
    User: action.payload?.user,
    });
  } else if (action.type === 'revoke_user_invite') {
    window.analytics.track('Revoked User Invite', {
      User: action.payload?.user,
    });
  } else if (action.type === 'resend_user_invite') {
    window.analytics.track('Resent User Invite', {
      User: action.payload?.user,
    });
  } else if (action.type === 'save_user') {
    window.analytics.track('Edited User Name/Email', {
      User: action.payload?.user,
    });
  } else if (action.type === 'fetch_user_locations_roles_success') {
    window.analytics.track('Managed User Location', {
      User: action.payload?.locations,
    });
  } else if (action.type === 'edit_user_roles') {
    window.analytics.track('Edited User Roles', {
      User: action.payload?.user,
    });
  } else if (action.type === 'remove_user_success') {
    window.analytics.track('Deleted User', {
      User: action.payload?.userId,
    });
  };
}

//Saga Functions: 
export function* identifyLoginSaga(){
  yield takeLatest(SIGNIN_USER_SUCCESS, identifyEventTriggerWorker);
}

export function* identifyHomePageSaga(){
  yield takeLatest(UPDATE_AUTH_USER_DATA_SUCCESS, identifyEventTriggerWorker);
}

export function* trackResetPasswordSaga() {
  yield takeLatest(REQUEST_RESET_PASSWORD, trackResetPasswordWorker);
}

export function* trackLocationFilterButtonSaga(){
  yield takeLatest(UPDATE_SHOW_LOCATION_FILTERS, trackLocationFilterButtonWorker);
}

export function* trackLocationFilterSetSaga(){
  yield takeLatest(UPDATE_LOCATION_FILTERS, trackLocationFilterSetWorker);
}

export function* trackLocationCheckboxSaga(){
  yield takeLatest(SELECT_LOCATION_ROW, trackLocationCheckboxSelectedWorker);
}

export function* trackAllLocationSelectedSaga(){
  yield takeLatest(SELECT_ALL_LOCATIONS, trackLocationCheckboxSelectedWorker);
}

export function* trackLocationThresholdSettingsSaga(){
  yield takeLatest(SAVE_THRESHOLD_SETTINGS_SUCCESS, trackLocationActionTakenWorker);
}

export function* trackLocationNotificationSettingsSaga(){
  yield takeLatest(UPDATE_NOTIFICATION_STATE, trackLocationActionTakenWorker);
}

export function* trackEditLocationSaga(){
  yield takeLatest(EDIT_LOCATION, trackLocationActionTakenWorker);
}

export function* trackLocationAddToParentSaga(){
  yield takeLatest(ADD_LOCATIONS_TO_PARENT, trackLocationActionTakenWorker);
}

export function* trackLocationRemoveSingleLocationSaga(){
  yield takeLatest(REMOVE_LOCATIONS, trackLocationCreatedOrRemovedWorker);
}

export function* trackLocationSetSystemModeBulkSaga(){
  yield takeLatest(SET_LOCATION_SYSTEM_MODE_BULK, trackLocationActionTakenWorker);
}

export function* trackLocationCreatedSaga(){
  yield takeLatest(CREATE_LOCATION, trackLocationCreatedOrRemovedWorker);
}

export function* trackViewAlertDetailSaga(){
  yield takeLatest(SET_CURRENT_ALERT, trackViewAlertDetailWorker);
}

export function* trackAlertsCheckboxRowSelectedSaga(){
  yield takeLatest(SELECT_ALERT_ROW, trackAlertsCheckboxSelectedWorker);
}

export function* trackAlertsCheckboxSelectAlledSaga(){
  yield takeLatest(SELECT_ALL_ALERTS, trackAlertsCheckboxSelectedWorker);
}

export function* trackAlertFilterSetSaga(){
  yield takeLatest(UPDATE_ALERT_FILTER, trackAlertFilterSetWorker);
}

export function* trackAlertExportButtonClickedSaga() {
  yield takeLatest(FETCH_ALERT_EXPORT_CSV_DATA, trackAlertExportButtonClickedWorker);
}

export function* trackActionOnSpecificAlertSaga() {
  yield takeLatest(CLEAR_ALERT_SUCCESS, trackActionOnSpecificAlertWorker);
}

export function* trackValveControlButtonSaga() {
  yield takeLatest(CHANGE_VALVE_STATE, trackValveControlButtonWorker);
}

export function* trackConsumptionChartScaleSetSaga() {
  yield takeLatest(FETCH_CONSUMPTION_BY_LOCATION_SUCCESS, trackConsumptionChartScaleSetWorker);
}

export function* trackRunHealthTestSaga() {
  yield takeLatest(RUN_HEALTH_TEST, trackRunHealthTestWorker);
}

export function* trackRenameDeviceSaga() {
  yield takeLatest(UPDATE_DEVICE, trackRenameDeviceWorker);
}

export function* trackUnpairDeviceSaga() {
  yield takeLatest(DELETE_DEVICE, trackUnpairDeviceWorker);
}

export function* trackDeveloperMenuOpenedSaga() {
  yield takeLatest(UPDATE_DEVELOPER_MENU_SETTINGS, trackDeveloperMenuOpenedWorker);
}

export function* trackAddUserSaga() {
  yield takeLatest(CREATE_USER, trackUserActionWorker);
}

export function* trackRevokeUserSaga() {
  yield takeLatest(REVOKE_USER_INVITE, trackUserActionWorker);
}

export function* trackResentInviteSaga() {
  yield takeLatest(RESEND_USER_INVITE, trackUserActionWorker);
}

export function* trackEditNameSaga() {
  yield takeLatest(SAVE_USER, trackUserActionWorker);
}

export function* trackManangeLocationSaga() {
  yield takeLatest(FETCH_USER_LOCATIONS_ROLES_SUCCESS, trackUserActionWorker);
}

export function* trackEditRoleSaga() {
  yield takeLatest(EDIT_USER_ROLES, trackUserActionWorker);
}

export function* trackRemoveUserSaga() {
  yield takeLatest(REMOVE_USER_SUCCESS, trackUserActionWorker);
}

export function* trackWaterUsageFilterSaga() {
  yield takeLatest(UPDATE_CONSUMPTION_FILTER, trackWaterUsageActionsWorker);
}

export function* trackWaterUsageRefreshButtonSaga() {
  yield takeLatest(FETCH_CONSUMPTION_BY_FILTERS_SUCCESS, trackWaterUsageActionsWorker);
}

export default function* rootSaga() {
  yield all([
    fork(identifyLoginSaga),
    fork(identifyHomePageSaga),
    fork(trackResetPasswordSaga),
    fork(trackLocationFilterButtonSaga),
    fork(trackLocationFilterSetSaga), 
    fork(trackLocationCheckboxSaga),
    fork(trackAllLocationSelectedSaga),
    fork(trackLocationThresholdSettingsSaga), 
    fork(trackViewAlertDetailSaga), 
    fork(trackLocationCreatedSaga),
    fork(trackLocationNotificationSettingsSaga),
    fork(trackEditLocationSaga),
    fork(trackLocationAddToParentSaga),
    fork(trackLocationRemoveSingleLocationSaga),
    fork(trackLocationSetSystemModeBulkSaga),
    fork(trackAlertFilterSetSaga),
    fork(trackAlertExportButtonClickedSaga),
    fork(trackActionOnSpecificAlertSaga),
    fork(trackValveControlButtonSaga),
    fork(trackConsumptionChartScaleSetSaga),
    fork(trackRunHealthTestSaga),
    fork(trackRenameDeviceSaga),
    fork(trackUnpairDeviceSaga),
    fork(trackDeveloperMenuOpenedSaga),
    fork(trackAddUserSaga),
    fork(trackRevokeUserSaga),
    fork(trackResentInviteSaga),
    fork(trackEditNameSaga),
    fork(trackManangeLocationSaga),
    fork(trackEditRoleSaga),
    fork(trackRemoveUserSaga),
    fork(trackWaterUsageFilterSaga),
    fork(trackWaterUsageRefreshButtonSaga),
    fork(trackAlertsCheckboxRowSelectedSaga), 
    fork(trackAlertsCheckboxSelectAlledSaga),
  ]);
}